import React from "react";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import NotificationList from "./NotificationList";
import NotificationEmptyState from "./NotificationEmptyState.js";
import LoadingButton from "@mui/lab/LoadingButton";
import { ListItemIcon, Menu, MenuItem, Stack } from "@mui/material";
import { CheckSquareOffset, DotsThree } from "@phosphor-icons/react";
import { useTheme } from "@emotion/react";

export default function NotificationDropdown({
  anchorEl,
  notifications,
  hasMore,
  loadingMore,
  onClose,
  onMarkAll,
  onLoadMore,
  onItemClick,
}) {
  const open = Boolean(anchorEl);
  const hasNotifications = notifications.length > 0;
  const hasUnreadNotifications = notifications.some((n) => !n.is_read);
  const [anchorMore, setAnchorMore] = React.useState(null);
  const handleMoreOpen = (e) => setAnchorMore(e.currentTarget);
  const handleMoreClose = () => setAnchorMore(null);
  const theme = useTheme();

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      scrollBar
      slotProps={{
        paper: {
          sx: {
            width: 500,
            borderRadius: "12px",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          },
        },
      }}
    >
      <Box sx={{ pb: "4px", px: "4px" }}>
        <Box display="flex" alignItems="center" py="8px" px="16px">
          <Typography variant="h3" px="8px" py="5px">
            Notifications
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: "auto" }}>
            {hasNotifications && !hasUnreadNotifications && (
              <Typography variant="body2" fontStyle="italic">
                <Box component="span" fontStyle="normal">
                  🎉
                </Box>{" "}
                No unread notifications
              </Typography>
            )}

            {hasNotifications && (
              <>
                <IconButton size="small" onClick={handleMoreOpen}>
                  <DotsThree color={theme.palette.text.secondary} size={20} />
                </IconButton>
                <Menu
                  anchorEl={anchorMore}
                  open={Boolean(anchorMore)}
                  onClose={handleMoreClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => {
                      onMarkAll();
                      handleMoreClose();
                    }}
                  >
                    <ListItemIcon>
                      <CheckSquareOffset weight="duotone" fontSize="small" />
                    </ListItemIcon>
                    Mark all as read
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Box>
        <Box
          sx={{
            overflowY: "auto",
            maxHeight: "90vh",
            px: "4px",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: "4px",
            },
          }}
        >
          {hasNotifications ? (
            <>
              <NotificationList notifications={notifications} onItemClick={onItemClick} />

              {hasMore && (
                <Box display="flex" justifyContent="center" p={1} mb="8px">
                  <LoadingButton
                    onClick={onLoadMore}
                    loading={loadingMore}
                    size="medium"
                    variant="text"
                  >
                    Read Older Notifications
                  </LoadingButton>
                </Box>
              )}
            </>
          ) : (
            <NotificationEmptyState />
          )}
        </Box>
      </Box>
    </Popover>
  );
}
